import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import rg4js from "raygun4js";
import { useEffect, useMemo, useRef, useState } from "react";
import { Container, Form } from "react-bootstrap";
import { ExclamationTriangleFill } from "react-bootstrap-icons";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import {
  CommunicationLanguage,
  CommunicationStatus,
  ConsumerTaskStatus,
  ObjectTypeEnum,
  type ViewCommunication,
  type ViewCommunicationItem,
} from "@/briefpoint-client";
import { useCommunicationApi, useDocumentApi } from "@/hooks/useApi";
import { useCollectFromClient } from "@/hooks/useCollectFromClient";
import {
  getCommunicationsForObjectOptions,
  getDocumentOptions,
} from "@/lib/query-options";
import { normalizeError } from "@/lib/utils";
import Button from "../Button";
import Loading from "../Loading";
import { FormModal } from "../Modals";
import { toast } from "../ui/toast";
import AddEditClientTaskModal from "./AddEditClientTaskModal";
import ReviewClientTaskBox from "./ReviewClientTaskBox";
import styles from "./ReviewClientTasks.module.scss";

export default function ReviewClientTasks() {
  const { documentId } = useParams<{ documentId: string }>();
  const history = useHistory();
  const location = useLocation();
  const documentApi = useDocumentApi();
  const communicationApi = useCommunicationApi();
  const queryClient = useQueryClient();
  const searchParams = new URLSearchParams(location.search);
  const [deleteCommunication, setDeleteCommunication] = useState<
    ViewCommunicationItem | undefined
  >(undefined);
  const [editCommunication, setEditCommunication] = useState<
    ViewCommunicationItem | undefined
  >(undefined);
  const [language, setLanguage] = useState<CommunicationLanguage>(
    CommunicationLanguage.EN,
  );
  //const [bulkTranslationComplete, setBulkTranslationComplete] = useState(false);
  const bulkTranslationComplete = useRef(false);
  const { hasAnySentCommunication } = useCollectFromClient();
  const documentQuery = useQuery({
    ...getDocumentOptions(documentId, documentApi),
    enabled: !!documentId,
  });

  const communicationsQuery = useQuery({
    ...getCommunicationsForObjectOptions(
      communicationApi,
      documentId,
      ObjectTypeEnum.Document,
    ),
    enabled: !!documentId,
  });

  const reviewCommunications = communicationsQuery.data;

  const activeCommunication = useMemo((): ViewCommunication | undefined => {
    return reviewCommunications?.find(
      (c) =>
        c.status !== CommunicationStatus.Closed &&
        c.status !== CommunicationStatus.Deleted,
    );
  }, [reviewCommunications]);

  // same `isSent` shape as useClientTasks: hook has no interrogatory here so activeCommunication stays local
  const isReviewLocked =
    activeCommunication?.status === CommunicationStatus.Sent ||
    activeCommunication?.status === CommunicationStatus.Open ||
    hasAnySentCommunication;

  const isNextStepBlocked =
    isReviewLocked &&
    activeCommunication?.status !== CommunicationStatus.Pending;

  function addTask() {
    if (isReviewLocked) return;
    setEditCommunication(undefined);
    searchParams.set("add_task", "true");
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }

  const startBulkTranslationMutation = useMutation({
    mutationFn: async () => {
      return communicationApi.communicationStartBulkTranslation({
        id: activeCommunication?.id ?? "",
        toLang: CommunicationLanguage.ES,
      });
    },
    onError: (error) => {
      toast.error("Failed to start bulk translation. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
  });

  const translationPollingQuery = useQuery({
    queryKey: [
      "translationPollingQuery",
      startBulkTranslationMutation.data?.id,
    ],
    queryFn: () =>
      communicationApi.communicationGetBulkTranslationTask({
        taskId: startBulkTranslationMutation.data?.id ?? "",
      }),
    enabled: !!startBulkTranslationMutation.data?.id,
    refetchInterval: (query) => {
      //TODO: change this out when the backend is updated to use the correct status type
      if (query.state.data?.status === ConsumerTaskStatus.Complete) {
        return false;
      }
      return 1500;
    },
    refetchIntervalInBackground: true,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  const deleteCommunicationMutation = useMutation({
    mutationFn: async (id: string | undefined) => {
      return communicationApi.communicationDeleteCommunicationItem({
        communicationId: activeCommunication?.id ?? "",
        communicationItemId: deleteCommunication?.id ?? id ?? "",
      });
    },
    onError: (error, _, context) => {
      // Restore previous data on error
      if (context?.previousData) {
        const queryKey = getCommunicationsForObjectOptions(
          communicationApi,
          documentId,
          ObjectTypeEnum.Document,
        ).queryKey;
        queryClient.setQueryData(queryKey, context.previousData);
      }
      toast.error("Failed to delete task. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      // Invalidate queries to ensure data is fresh
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
      setDeleteCommunication(undefined);
    },
    onMutate: async (id: string | undefined) => {
      //optimistically delete the task if id is defined
      if (!id) return;

      const queryKey = getCommunicationsForObjectOptions(
        communicationApi,
        documentId,
        ObjectTypeEnum.Document,
      ).queryKey;

      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousData =
        queryClient.getQueryData<ViewCommunication[]>(queryKey);

      // Optimistically update to remove the task
      queryClient.setQueryData<ViewCommunication[]>(queryKey, (old) => {
        if (!old) return old;
        return old.map((communication) => {
          if (
            communication.externalCommunicationItems?.some(
              (item) => item.id === id,
            )
          ) {
            return {
              ...communication,
              externalCommunicationItems:
                communication.externalCommunicationItems.filter(
                  (item) => item.id !== id,
                ),
            };
          }
          return communication;
        });
      });

      // Return context with previousData for rollback
      return { previousData };
    },
  });

  const deleteInterrogatoryMutation = useMutation({
    mutationFn: async ({
      communicationId,
      interrogatoryId,
    }: {
      communicationId: string;
      interrogatoryId: number;
    }) => {
      const communicationText =
        activeCommunication?.externalCommunicationItems?.find(
          (c) => c.id === communicationId,
        )?.communication;
      const removedAssociatedObjectIds =
        activeCommunication?.externalCommunicationItems
          ?.find((c) => c.id === communicationId)
          ?.responseInfo?.associatedObjectIds?.filter(
            (id) => Number(id) !== interrogatoryId,
          );
      return communicationApi.communicationPutCommunicationItem({
        id: activeCommunication?.id ?? "",
        putCommunicationItem: {
          id: communicationId,
          objectType: ObjectTypeEnum.Interrogatory,
          objectId: interrogatoryId.toString(),
          communication: communicationText ?? "",
          language: CommunicationLanguage.EN,
          responseInfo: {
            associatedObjectIds: removedAssociatedObjectIds?.map((id) =>
              id.toString(),
            ),
          },
        },
      });
    },
    onSuccess: () => {
      toast.success("Task deleted successfully");
    },
    onError: (error, _, context) => {
      if (context?.previousData) {
        const queryKey = getCommunicationsForObjectOptions(
          communicationApi,
          documentId,
          ObjectTypeEnum.Document,
        ).queryKey;
        queryClient.setQueryData(queryKey, context.previousData);
      }
      toast.error("Failed to delete task. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
    },
    onMutate: async ({
      communicationId,
      interrogatoryId,
    }: {
      communicationId: string;
      interrogatoryId: number;
    }) => {
      const queryKey = getCommunicationsForObjectOptions(
        communicationApi,
        documentId,
        ObjectTypeEnum.Document,
      ).queryKey;

      await queryClient.cancelQueries({ queryKey });

      const previousData =
        queryClient.getQueryData<ViewCommunication[]>(queryKey);

      queryClient.setQueryData<ViewCommunication[]>(queryKey, (old) => {
        if (!old) return old;
        return old.map((communication) => {
          if (
            !communication.externalCommunicationItems?.some(
              (item) => item.id === communicationId,
            )
          ) {
            return communication;
          }
          return {
            ...communication,
            externalCommunicationItems:
              communication.externalCommunicationItems?.map((item) => {
                if (item.id !== communicationId) return item;
                return {
                  ...item,
                  responseInfo: {
                    ...item.responseInfo,
                    associatedObjectIds:
                      item.responseInfo?.associatedObjectIds?.filter(
                        (id) => Number(id) !== interrogatoryId,
                      ) ?? [],
                  },
                };
              }),
          };
        });
      });

      return { previousData };
    },
  });

  const updateCommunicationMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      return communicationApi.communicationUpdateCommunication({
        id: activeCommunication?.id ?? "",

        putClientCommunication: {
          status: CommunicationStatus.Pending,
          communicationInfo: {
            ...activeCommunication?.communicationInfo,
            requestedReviewIds: ids,
          },
        },
      });
    },
    onError: (error) => {
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
      history.push(
        `/documents/${documentId}/client-feedback/${activeCommunication?.id}/confirm-client-info`,
      );
    },
  });

  function saveAndRedirectToReviewFlow() {
    if (isNextStepBlocked) return;
    const requestedReviewIds = activeCommunication?.externalCommunicationItems
      ?.map((i) => i.responseInfo?.associatedObjectIds?.map((id) => Number(id)))
      .reduce((acc, curr) => acc?.concat(curr ?? []) ?? [], []);

    updateCommunicationMutation.mutate(requestedReviewIds ?? []);
  }

  useEffect(() => {
    // wait for polling to finish and then refetch the communications, send error to raygun if the task failed
    if (
      startBulkTranslationMutation.isSuccess &&
      (translationPollingQuery.data?.status === ConsumerTaskStatus.Complete ||
        translationPollingQuery.data?.status === ConsumerTaskStatus.Error) &&
      !bulkTranslationComplete.current
    ) {
      if (translationPollingQuery.data?.status === ConsumerTaskStatus.Error) {
        toast.error("Failed to translate the task. Please try again.");
        rg4js("send", {
          error: normalizeError(translationPollingQuery.error),
        });
      }
      bulkTranslationComplete.current = true;
      communicationsQuery.refetch();
    }
  }, [
    startBulkTranslationMutation.isSuccess,
    translationPollingQuery.data?.status,
    communicationsQuery,
    translationPollingQuery.error,
  ]);

  useEffect(() => {
    if (!isReviewLocked) return;
    const nextParams = new URLSearchParams(location.search);
    if (nextParams.get("add_task") !== "true") return;
    nextParams.delete("add_task");
    history.replace({
      pathname: location.pathname,
      search: nextParams.toString(),
    });
  }, [history, isReviewLocked, location.pathname, location.search]);

  const reviewTasksPanelWidth =
    language === CommunicationLanguage.ES ? "996px" : "746px";

  return (
    <Container className="page-container">
      <AddEditClientTaskModal
        interrogatories={documentQuery.data?.interrogatories ?? []}
        communicationId={activeCommunication?.id ?? ""}
        externalCommunication={editCommunication}
        language={language}
      />
      <FormModal
        cancelText="No, go back"
        confirmText="Yes, delete"
        show={!!deleteCommunication}
        onClose={() => setDeleteCommunication(undefined)}
        onConfirm={() => deleteCommunicationMutation.mutate(undefined)}
        cancelButtonVariant="outline-primary"
        title="Are you sure you want to delete?"
      >
        <div>
          This task is linked to one or more requests. Deleting it will
          permanently remove those associations.
        </div>
      </FormModal>
      <Loading isLoading={documentQuery.isLoading}>
        <div
          data-testid="review-client-tasks-breadcrumbs"
          className="bread-crumbs mb-3"
        >
          <Link
            to={`/wizard/${documentId}/${documentQuery.data?.interrogatories.length}`}
          >
            Review
          </Link>{" "}
          {"> Client Tasks"}
        </div>
        <div className="mb-3">
          <h1
            data-testid="review-client-tasks-header"
            className="feedback-header"
          >
            Review Client Tasks
          </h1>
        </div>
        {isReviewLocked ? (
          <div
            className={styles.warningBox}
            style={{ width: reviewTasksPanelWidth }}
            data-testid="review-client-tasks-locked-banner"
          >
            <ExclamationTriangleFill className="me-2" color="#CEA552" />
            Client response in progress, editing disabled.
          </div>
        ) : null}
        <div
          data-testid="review-client-tasks-document-name"
          className="fw-bold fs-4"
        >
          {documentQuery.data?.name}
        </div>
        <div className="pt-3 pb-2">
          <div>
            <label htmlFor="select-english" className="fs-6 fw-bold mb-0">
              Client's Primary Language
            </label>
          </div>
          <div className="d-inline-flex align-items-center">
            <Form.Check
              label="English"
              type="radio"
              inline
              disabled={isReviewLocked}
              checked={language === CommunicationLanguage.EN}
              onChange={() => {
                setLanguage(CommunicationLanguage.EN);
              }}
              id="select-english"
            />
            <Form.Check
              label="Spanish"
              type="radio"
              inline
              disabled={isReviewLocked}
              checked={language === CommunicationLanguage.ES}
              onChange={() => {
                setLanguage(CommunicationLanguage.ES);
                startBulkTranslationMutation.mutate();
              }}
              id="select-spanish"
            />
          </div>
        </div>
        <div
          className={`mb-4 ${styles.reviewTasksPanel}`}
          style={{ width: reviewTasksPanelWidth }}
        >
          <div>
            {activeCommunication?.externalCommunicationItems
              ?.sort(
                (a, b) =>
                  a.communication?.localeCompare(b.communication ?? "") ?? 0,
              )
              ?.filter(
                (c) => (c.responseInfo?.associatedObjectIds?.length || 0) > 0,
              )
              ?.map((externalCommunication) => (
                <ReviewClientTaskBox
                  key={externalCommunication.id}
                  communication={externalCommunication}
                  language={language}
                  interrogatories={documentQuery.data?.interrogatories ?? []}
                  removeInterrogatory={(interrogatoryId) => {
                    deleteInterrogatoryMutation.mutate({
                      communicationId: externalCommunication.id ?? "",
                      interrogatoryId: Number(interrogatoryId),
                    });
                  }}
                  setDeleteCommunication={(externalCommunication) =>
                    setDeleteCommunication(externalCommunication)
                  }
                  translatedCommunication={activeCommunication?.externalCommunicationItems?.find(
                    (c) =>
                      c.translatedExternalCommunicationItemId ===
                      externalCommunication.id,
                  )}
                  documentId={documentId}
                  setEditCommunication={(communication) => {
                    setEditCommunication(communication);
                    searchParams.set("add_task", "true");
                    history.push({
                      pathname: location.pathname,
                      search: searchParams.toString(),
                    });
                  }}
                />
              ))}
            <Button
              data-testid="add-task-button"
              variant="outline"
              size="sm"
              disabled={isReviewLocked}
              onClick={addTask}
            >
              + Add Task
            </Button>

            {(activeCommunication?.externalCommunicationItems?.filter((c) => {
              return (
                !c?.responseInfo?.associatedObjectIds?.length &&
                !c.translatedExternalCommunicationItemId
              );
            })?.length ?? 0) > 0 && (
              <div className="mt-4">
                <div>
                  <div className="d-flex mb-1 align-items-center">
                    <ExclamationTriangleFill
                      style={{ color: "var(--feedback-warning-dark)" }}
                    />
                    <div className="fs-3 ms-2">Unassigned Tasks</div>
                  </div>
                  <div className="fs-6 mb-2">
                    The following items have no associated requests and won’t be
                    visible to your client. Add at least one request to make
                    them available.
                  </div>
                </div>
                {activeCommunication?.externalCommunicationItems
                  ?.filter(
                    (c) =>
                      !c.responseInfo?.associatedObjectIds?.length &&
                      !c.translatedExternalCommunicationItemId,
                  )
                  ?.map((externalCommunication) => (
                    <ReviewClientTaskBox
                      key={externalCommunication.id}
                      communication={externalCommunication}
                      language={language}
                      interrogatories={
                        documentQuery.data?.interrogatories ?? []
                      }
                      documentId={documentId}
                      setDeleteCommunication={(externalCommunication) =>
                        deleteCommunicationMutation.mutate(
                          externalCommunication.id,
                        )
                      }
                      setEditCommunication={(communication) => {
                        setEditCommunication(communication);
                        searchParams.set("add_task", "true");
                        history.push({
                          pathname: location.pathname,
                          search: searchParams.toString(),
                        });
                      }}
                    />
                  ))}
              </div>
            )}
          </div>
          <div
            className={clsx(
              "d-flex justify-content-end pt-2",
              isReviewLocked &&
                activeCommunication?.status ===
                  CommunicationStatus.Pending &&
                styles.reviewTasksNextAboveLockedOverlay,
            )}
          >
            <Button
              data-testid="next-button"
              variant="default"
              disabled={isNextStepBlocked}
              onClick={saveAndRedirectToReviewFlow}
            >
              Next
            </Button>
          </div>
          {isReviewLocked ? (
            <div
              className={styles.reviewTasksOverlay}
              aria-hidden
              data-testid="review-client-tasks-sent-overlay"
            />
          ) : null}
        </div>
      </Loading>
    </Container>
  );
}
